import { useEffect, useState } from 'react'
import { Flame, Trophy, X, Users, Gift, ChevronRight } from 'lucide-react'
import { checkinGamify, getEarnCatalog, type GamifyState, type Achievement, type EarnRule } from '@/api/gamify'
import { cn } from '@/lib/utils'

// Streak + Health Coins strip. On mount it records the daily check-in (server caps to once/day),
// then celebrates any coins earned or achievements unlocked. Tap to see the full progress sheet.
export function GamificationBar({ userId, onOpenLeaderboard, onOpenRewards }: {
  userId: string
  onOpenLeaderboard: () => void
  onOpenRewards: () => void
}) {
  const [state, setState] = useState<GamifyState | null>(null)
  const [open, setOpen] = useState(false)
  const [celebrate, setCelebrate] = useState<{ coins: number; achs: Achievement[] } | null>(null)

  useEffect(() => {
    let alive = true
    checkinGamify(userId).then((res) => {
      if (!alive || !res) return
      setState(res.state)
      if (res.awarded_coins > 0 || res.new_achievements.length) {
        setCelebrate({ coins: res.awarded_coins, achs: res.new_achievements })
        setTimeout(() => alive && setCelebrate(null), 3800)
      }
    })
    return () => { alive = false }
  }, [userId])

  if (!state) return null
  const pct = state.level.next
    ? Math.min(100, Math.round((state.lifetime / state.level.next.at) * 100))
    : 100

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="lift flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-2.5 text-left focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <span className="inline-flex items-center gap-1.5">
          <Flame className={cn('size-5', state.streak > 0 ? 'text-alert-text' : 'text-muted-foreground')} />
          <span className="text-sm font-semibold text-foreground">{state.streak}</span>
          <span className="text-[11px] text-muted-foreground">day{state.streak === 1 ? '' : 's'}</span>
        </span>
        <span className="h-5 w-px bg-border" />
        <span className="inline-flex items-center gap-1.5">
          <Coin className="size-4" />
          <span className="text-sm font-semibold text-foreground">{state.coins.toLocaleString()}</span>
          <span className="text-[11px] text-muted-foreground">coins</span>
        </span>
        <span className="ml-auto inline-flex items-center gap-1.5">
          <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-medium text-accent-foreground">{state.level.name}</span>
          <Trophy className="size-4 text-muted-foreground" />
        </span>
      </button>

      {celebrate && (
        <div className="pointer-events-none absolute inset-x-0 top-16 z-50 flex justify-center animate-fade-up">
          <div className="pointer-events-auto rounded-2xl border border-border bg-card px-4 py-3 shadow-lg">
            {celebrate.coins > 0 && (
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Coin className="size-5" /> +{celebrate.coins} Health Coins · {state.streak}-day streak 🔥
              </div>
            )}
            {celebrate.achs.map((a) => (
              <div key={a.id} className="mt-1 text-sm text-foreground">{a.emoji} Unlocked: <span className="font-semibold">{a.label}</span></div>
            ))}
          </div>
        </div>
      )}

      {open && (
        <ProgressSheet
          state={state} pct={pct} onClose={() => setOpen(false)}
          onOpenLeaderboard={() => { setOpen(false); onOpenLeaderboard() }}
          onOpenRewards={() => { setOpen(false); onOpenRewards() }}
        />
      )}
    </>
  )
}

function ProgressSheet({ state, pct, onClose, onOpenLeaderboard, onOpenRewards }: {
  state: GamifyState; pct: number; onClose: () => void
  onOpenLeaderboard: () => void; onOpenRewards: () => void
}) {
  const ALL: Achievement[] = [
    { id: 'first_steps', label: 'First steps', emoji: '🌱' },
    { id: 'connected', label: 'Data connected', emoji: '🔗' },
    { id: 'week_streak', label: '7-day streak', emoji: '🔥' },
    { id: 'century', label: '100 coins', emoji: '💯' },
    { id: 'month_streak', label: '30-day streak', emoji: '🏆' },
  ]
  const earned = new Set(state.achievements.map((a) => a.id))
  const [earnCatalog, setEarnCatalog] = useState<EarnRule[]>([])
  const [showEarnInfo, setShowEarnInfo] = useState(false)
  useEffect(() => { getEarnCatalog().then(setEarnCatalog) }, [])

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-overlay p-4" onClick={onClose}>
      <div className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl bg-card p-5 shadow-lg animate-scale-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-foreground">Your progress</h2>
            <p className="text-[11px] text-muted-foreground">Earn coins for healthy actions — grounded in your real data.</p>
          </div>
          <button onClick={onClose} className="-mr-1.5 rounded-md p-1.5 hover:bg-muted" aria-label="Close"><X className="size-5 text-foreground" /></button>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <Stat icon={<Flame className="size-4 text-alert-text" />} value={`${state.streak}`} label="Current streak" />
          <Stat icon={<Coin className="size-4" />} value={state.coins.toLocaleString()} label="Coins" />
          <Stat icon={<Trophy className="size-4 text-muted-foreground" />} value={state.longest.toString()} label="Longest streak" />
        </div>

        {/* Level progress */}
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="font-medium text-foreground">Level: {state.level.name}</span>
            {state.level.next && <span className="text-muted-foreground">{state.lifetime}/{state.level.next.at} → {state.level.next.name}</span>}
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full sara-gradient" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* Leaderboard + Rewards entry points */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={onOpenLeaderboard} className="lift flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2.5 text-left hover:border-ring">
            <Users className="size-4 text-info-text" />
            <span className="text-sm font-medium text-foreground">Friends</span>
          </button>
          <button onClick={onOpenRewards} className="lift flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2.5 text-left hover:border-ring">
            <Gift className="size-4 text-ornament-plum" />
            <span className="text-sm font-medium text-foreground">Redeem</span>
          </button>
        </div>

        {/* How to earn coins */}
        <div className="mt-4">
          <button onClick={() => setShowEarnInfo((v) => !v)} className="flex w-full items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">How to earn coins</h3>
            <ChevronRight className={cn('size-3.5 text-muted-foreground transition-transform', showEarnInfo && 'rotate-90')} />
          </button>
          {showEarnInfo && (
            <div className="mt-2 space-y-1.5">
              {earnCatalog.map((r) => (
                <div key={r.kind} className="flex items-center gap-2.5 rounded-lg border border-border-subtle bg-background px-3 py-2">
                  <span className="shrink-0 rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-bold text-accent-foreground">+{r.coins}</span>
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-foreground">{r.label}</div>
                    <div className="text-[10px] text-muted-foreground">{r.description}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Achievements */}
        <div className="mt-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Achievements</h3>
          <div className="grid grid-cols-1 gap-2">
            {ALL.map((a) => {
              const has = earned.has(a.id)
              return (
                <div key={a.id} className={cn('flex items-center gap-3 rounded-lg border px-3 py-2', has ? 'border-border bg-background' : 'border-border-subtle opacity-55')}>
                  <span className="text-lg">{a.emoji}</span>
                  <span className="text-sm text-foreground">{a.label}</span>
                  {has && <span className="ml-auto text-[11px] font-medium text-success-text">Unlocked</span>}
                </div>
              )
            })}
          </div>
        </div>
        <p className="mt-4 text-[11px] text-muted-foreground">SARA celebrates effort, never risky behavior — and coins only come from verified activity.</p>
      </div>
    </div>
  )
}

function Stat({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="rounded-lg border border-border-subtle bg-background px-3 py-2 text-center">
      <div className="flex items-center justify-center gap-1">{icon}<span className="text-base font-semibold text-foreground">{value}</span></div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  )
}

// Simple coin glyph in the SARA gradient.
function Coin({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="coin-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFC208" /><stop offset="1" stopColor="#EB5324" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="9" fill="url(#coin-g)" />
      <circle cx="12" cy="12" r="9" fill="none" stroke="#fff" strokeOpacity="0.5" strokeWidth="1.2" />
      <path d="M12 7v10M9.2 9.2c0-1.1 1.3-1.7 2.8-1.7s2.8.6 2.8 1.9c0 2.6-5.6 1-5.6 3.6 0 1.3 1.3 1.9 2.8 1.9s2.8-.6 2.8-1.7" fill="none" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}
